// packages/core/src/learning/schools/keynesian-mmt.ts
// ─────────────────────────────────────────────────────────────────────────
// Authored exhibit for "Keynesian / MMT". Matches boglehead-indexing.ts gold
// standard.
//
// ID-bound fields (the validator + compiler guard these):
//   - figures:        FigureId[]  — empty in source AND kept []. The worldview
//                     names no figures. Canon authors (John Maynard Keynes,
//                     Abba Lerner, Warren Mosler, Stephanie Kelton, Wynne
//                     Godley) are NOT figures-tally entries per the protocol,
//                     and none currently exist as a FigureId in
//                     figureRegistry.ts. This school contributes nothing to the
//                     running figure tally. (This is the counterpart school to
//                     hard-money; both macro-policy schools ship figure-less.)
//   - applyActions:   ApplyAction union — type-checked at compile time. Only
//                     live/allowed action types emitted.
//   - featuredLessonIds / debateIds — [] until the cross-ref pass.
// ─────────────────────────────────────────────────────────────────────────

import type { SchoolExhibitEntry } from '../SchoolExhibitMeta';

export const KEYNESIAN_MMT_EXHIBIT: SchoolExhibitEntry = {
	id: 'keynesian-mmt',
	tagline: 'Keynesian and MMT believes public money can shape real life.',
	worldviewSummary:
		'Keynesian and MMT is paying attention to demand, jobs, public capacity, deficits, currency power, and what governments can do during weak economies. This lens protects you from thinking the economy works like a household budget. It accepts that public spending can support employment, infrastructure, stability, and private income when the economy has unused capacity. The blind spot is underweighting inflation risk, political misuse, poor execution, and the real limits of labor, housing, energy, materials, and trust. This lens is strongest when it helps users understand public money as a tradeoff between capacity, inflation, fairness, and results.',
	coreBeliefs: [
		'Keynesian and MMT believes government budgets are not the same as household budgets.',
		'Keynesian and MMT believes weak demand can leave workers, businesses, and communities below their potential.',
		'Keynesian and MMT believes public spending can support private income when resources are unused.',
		'Keynesian and MMT believes the real limit is not only the deficit number. It is inflation, capacity, politics, and trust.',
		'Keynesian and MMT believes taxes do more than fund spending. They can shape demand, incentives, and distribution.',
		'Keynesian and MMT believes unemployment is not just personal failure. It can be a system level signal.',
	],
	warnsAgainst: [
		'Treating national budgets like a family checking account.',
		'Using deficit fear to ignore unemployment, infrastructure, health, and public capacity.',
		'Assuming markets always return everyone to useful work quickly on their own.',
		'Hand waving inflation, waste, politics, and execution risk when public spending expands.',
	],
	helps: 'This lens helps people understand recessions, public spending, deficits, jobs, inflation debates, and why macro policy affects normal households. It may not fit when someone wants a simple rule like deficits are always bad or spending has no limits.',
	figures: [],
	canon: [
		{
			title: 'The General Theory of Employment, Interest and Money',
			author: 'John Maynard Keynes',
			oneLineIdea:
				'Weak demand can keep people and businesses below full use, so public policy can matter during downturns.',
		},
		{
			title: 'Functional Finance and the Federal Debt',
			author: 'Abba Lerner',
			oneLineIdea:
				'Public finance should be judged by economic results, not by whether it resembles household budgeting.',
		},
		{
			title: 'Soft Currency Economics',
			author: 'Warren Mosler',
			oneLineIdea:
				'A currency issuing government has different constraints than a household, but still faces real resource limits.',
		},
		{
			title: 'The Deficit Myth',
			author: 'Stephanie Kelton',
			oneLineIdea:
				'Deficits are not automatically the binding limit. Inflation and real capacity are the harder questions.',
		},
		{
			title: 'Sectoral balances framework',
			author: 'Wynne Godley',
			oneLineIdea:
				'One sector’s deficit can be another sector’s surplus, so government balances connect to private saving and foreign trade.',
		},
	],
	featuredLessonIds: [],
	nativeModuleId: 'school-keynesian-mmt',
	debateIds: [],
	applyActions: [
		{
			label: 'See how public shocks show up in your current picture',
			action: {
				type: 'openDashboard',
				label: 'Review your current picture',
			},
		},
		{
			label: 'Test how higher costs or lower income change your path',
			action: {
				type: 'openSimulator',
				label: 'Simulate macro pressure',
				scenarioType: 'custom',
			},
		},
		{
			label: 'Check how much runway you have if the economy weakens',
			action: {
				type: 'calculateRunway',
				label: 'Check your runway',
			},
		},
		{
			label: 'Name what public money should actually accomplish',
			action: {
				type: 'noToolAction',
				label: 'Judge the public tradeoff',
				prompt: 'Write one public spending idea, what real problem it tries to solve, and what risk or limit must be watched.',
			},
		},
	],
	marketingHooks: [
		{
			line: 'The government budget is not your household budget. That does not mean it has no limits.',
			kind: 'hook',
		},
		{
			line: 'The hidden cost of deficit panic can be unemployed people and broken public systems.',
			kind: 'hook',
		},
		{
			line: 'When should a government spend more, and when does that spending become inflation pressure?',
			kind: 'interview-question',
		},
		{
			line: 'What public investment helped your life more than people usually admit?',
			kind: 'interview-question',
		},
		{
			line: 'Nisk helps people see macro money debates without turning them into slogans.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'This school teaches why public money, jobs, inflation, and private income are connected.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'Show why a country that issues currency is not the same as a family with a credit card.',
			kind: 'creator-prompt',
		},
		{
			line: 'Ask people whether they fear deficits more than broken roads, job loss, or rising rent.',
			kind: 'creator-prompt',
		},
	],
	revision: 1,
};
